// ─── productsData.js ──────────────────────────────────────────────────────────
// Single source of truth for all product cards, detail pages and the header
// dropdown. Content is derived faithfully from each PPT deck.

export const productsData = [
  // ══════════════════════════════════════════════════════════════════════════
  // 1. DISTRIBUTOR CRM
  // ══════════════════════════════════════════════════════════════════════════
  {
    slug: "distributor-crm-hotelpos-style",
    title: "Distribution Management System",
    description: "Integrated distribution business management — sales, procurement, inventory, finance, HRMS and field operations in one platform.",
    file: "/ppts/distributor-crm-hotelpos-style.pptx",
    content: [
      {
        heading: "What is Distributor CRM?",
        body: [
          "Distributor CRM is a unified business system for managing products, dealers, suppliers, orders, procurement, stock, invoicing, collections, accounts, payroll and field operations.",
          "The platform covers 13 main sections and over 97 unique views, supporting 12 user roles with granular permission controls.",
          "It provides centralised product and partner records, connected sales and purchasing workflows, warehouse and ledger visibility, controlled permissions and approvals, and management reports from shared data.",
        ],
        images: [],
      },
      {
        heading: "Platform at a Glance",
        body: [
          "Configure: category, product, pricing, warehouse, dealer and supplier masters.",
          "Sell: sales orders, discounts, schemes, dealer invoices and collections.",
          "Procure: supplier records, purchase orders, GRN, supplier invoices and payments.",
          "Track stock: stock status, shortages, damaged stock and inter-warehouse transfers.",
          "Manage people: user permissions, HRMS, biometric/geolocation attendance and payroll.",
          "Analyse: ledgers, profit reports, GST, receivables/payables and audit logs.",
        ],
        images: [
          { src: "/product-images/distributor-crm-hotelpos-style/image2.png", alt: "Distributor CRM platform overview" },
        ],
      },
      {
        heading: "Initial Configuration & Masters",
        body: [
          "Set up the catalogue, business partners and price rules before raising transactions.",
          "Build a searchable brand → category → subcategory hierarchy that feeds products, filters, discount rules and schemes.",
          "Create products with mandatory brand, category and subcategory; capture name, code, unit, GST rate, MRP and unit price.",
          "Maintain a dealer network with credit limits, regular/CD credit days, territories and discount mappings.",
          "Onboard suppliers with GSTIN, payment terms, credit days and bank details.",
          "Configure region master, route master, warehouse master and service charge master for consistent cross-module operations.",
        ],
        images: [
          { src: "/product-images/distributor-crm-hotelpos-style/image6.png", alt: "Product and dealer master setup" },
          { src: "/product-images/distributor-crm-hotelpos-style/image4.png", alt: "Dealer and supplier master forms" },
        ],
      },
      {
        heading: "Order to Cash",
        body: [
          "The order-to-cash workflow: dealer → sales order → credit check → invoice → collections → ledger.",
          "Sales order dashboard provides KPIs on value, pending orders and average credit days, with filtering by dealer, product, stock and status.",
          "Dealer-specific discounts and promotional schemes are configured once and applied consistently across invoices — no ad hoc spreadsheet entries.",
          "Approved invoices connect receivables, GST and financial ledgers automatically.",
          "Payment allocation matches booked receipts to outstanding invoices without double-posting.",
          "Dealer ledger gives a full financial history with invoice value, payments received and remaining balance.",
        ],
        images: [
          { src: "/product-images/distributor-crm-hotelpos-style/image10.png", alt: "Sales order dashboard" },
          { src: "/product-images/distributor-crm-hotelpos-style/image18.png", alt: "Invoice and collections view" },
        ],
      },
      {
        heading: "Procurement & Inventory",
        body: [
          "Procure-to-pay workflow: supplier → purchase order → GRN → stock → supplier bill → payments.",
          "PO management lets you review all orders, filter by date, supplier, warehouse and status, and export order data.",
          "GRN entry distinguishes partial vs completed receipts; guided receiving captures delivered, damaged and accepted quantities.",
          "Stock management shows net quantity, damaged stock and low-stock items per warehouse, with physical, blocked and available quantity buckets.",
          "Stock transfers between warehouses, stock adjustments for physical-count corrections, and purchase wishlists for proactive reordering.",
        ],
        images: [
          { src: "/product-images/distributor-crm-hotelpos-style/image22.png", alt: "Procurement and stock management" },
          { src: "/product-images/distributor-crm-hotelpos-style/image24.png", alt: "GRN and inventory view" },
        ],
      },
      {
        heading: "Finance, Reports & Accounts",
        body: [
          "Voucher entry for journal, contra, payment and receipt vouchers; posting flows directly into ledgers.",
          "Financial reports include day book, trial balance, profit & loss, balance sheet, general ledger, cash flow and funds flow statements.",
          "GST reports, GSTR-2B reconciliation, TDS management, books health check, year-end checklist and audit trail.",
          "Auto reconciliation matches book entries against bank records to reduce manual effort.",
          "Cash/bank book with daily running balances; cheque management through receipt, deposit, clearance and bounce lifecycle.",
        ],
        images: [
          { src: "/product-images/distributor-crm-hotelpos-style/image25.png", alt: "Finance and accounts dashboard" },
          { src: "/product-images/distributor-crm-hotelpos-style/image27.png", alt: "Reports and analytics" },
        ],
      },
      {
        heading: "HRMS & User Control",
        body: [
          "User management with role, region and module-level permission controls — a sales executive gets sales app access, not the complete finance ledger.",
          "Employee registration covers department, designation, salary type and payroll base rate.",
          "Attendance modules: geo attendance monitoring, biometric punches, shift rules, absent review and HRMS settings.",
          "Leave policy defines eligibility and accrual; leave balances track available, used and adjusted leave per employee.",
          "Salary breakdown connects gross pay, overtime, deductions, absent penalties and net salary; bulk salary slip generation at month-end.",
        ],
        images: [
          { src: "/product-images/distributor-crm-hotelpos-style/image31.png", alt: "HRMS and payroll module" },
        ],
      },
      {
        heading: "Field Sales & Delivery Apps",
        body: [
          "Sales Executive App: dealer visits, route planning, live GPS tracking, collections, target management, expense claims and daily performance summaries.",
          "Delivery Executive App: delivery assignment, monitoring, proof of delivery, live tracking, route planning and delivery history.",
          "Live tracking screen shows active, GPS-off and signal-lost states; managers can locate the nearest executive to a dealer.",
          "Dealer app allows dealers to submit order requests from their phones; the back office converts approved requests into sales orders with no re-typing.",
        ],
        images: [],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 2. MANUFACTURING ERP
  // ══════════════════════════════════════════════════════════════════════════
  {
    slug: "manufacturing-erp",
    title: "Manufacturing Firm ERP",
    description: "Procurement, inventory, warehouse operations and Tally accounting integration in one connected workspace.",
    file: "/ppts/manufacturing-erp.pptx",
    content: [
      {
        heading: "What is Manufacturing ERP?",
        body: [
          "Manufacturing ERP is a connected workspace for purchasing, stock visibility and accounting workflows.",
          "It brings supplier and client records, purchase orders, goods receipts, stock items and warehouse information into one place.",
          "Key modules: Procurement (vendors, clients, purchase orders), Inventory (SKUs, quantities, reorder levels), Warehouse (locations, capacity, GRNs) and Tally Integration (import, export, status and logs).",
        ],
        images: [
          { src: "/product-images/manufacturing-erp/image1.png", alt: "Manufacturing ERP dashboard" },
        ],
      },
      {
        heading: "Dashboard & Navigation",
        body: [
          "The dashboard provides a single starting point to review daily activity: purchase order totals, dealer orders, approvals, inventory items, active vendors and GRNs pending QC.",
          "Tally sync status and voucher summaries are visible directly on the dashboard.",
          "Top navigation allows searching orders, SKUs and vendors, and provides quick access to Create, Tally, notifications and the Admin profile menu.",
        ],
        images: [
          { src: "/product-images/manufacturing-erp/image2.png", alt: "ERP dashboard overview" },
        ],
      },
      {
        heading: "Procurement & Supplier Management",
        body: [
          "Vendor management: view, search by keyword, filter by status or category; add, edit or delete vendor records.",
          "Vendor form captures company and contact details, classification, location, GST number and payment terms.",
          "Client records store contact, address, tax and classification information.",
          "Purchase orders are built with vendor, linked RFQ, delivery date, payment terms, line items (name, quantity, unit, base price, GST) and automatic totals.",
          "Goods Receipt Notes (GRN) track receipt progress against POs and record the receiving warehouse and staff member.",
        ],
        images: [
          { src: "/product-images/manufacturing-erp/image3.png", alt: "Vendor management screen" },
          { src: "/product-images/manufacturing-erp/image13.png", alt: "Purchase order creation" },
        ],
      },
      {
        heading: "Inventory & Warehouse Management",
        body: [
          "Stock dashboard summarises total stock units, low-stock items, dead stock and active SKUs.",
          "Create stock items (SKUs) with unique code, name, category, unit, opening quantity, warehouse, pricing, GST, HSN code and reorder point.",
          "Register warehouses with ID, name, location, capacity, manager and contact details.",
          "Add stock from purchase orders by referencing an SKU, entering received quantity, reorder level, unit, category and warehouse; a manual stock entry invoice is auto-generated.",
          "Recent stock movements and low-stock alerts are visible on the dashboard.",
        ],
        images: [
          { src: "/product-images/manufacturing-erp/image14.png", alt: "Inventory stock dashboard" },
          { src: "/product-images/manufacturing-erp/image15.png", alt: "SKU and warehouse setup" },
        ],
      },
      {
        heading: "Tally Integration — Key Differentiator",
        body: [
          "A dedicated workspace for controlled, manual data exchange between Manufacturing ERP and Tally.",
          "Import from Tally: bring vendors, clients, ledgers and vouchers into ERP.",
          "Export to Tally: push sales invoices, masters, purchase orders and payment vouchers from ERP to Tally using the XML import API.",
          "The user explicitly chooses the direction — Import (Tally → ERP) or Export (ERP → Tally) — preventing accidental data movement.",
          "Export can create or alter records in Tally; always review the confirmation dialog and match records carefully before running.",
        ],
        images: [],
      },
      {
        heading: "Access Control & Workflow",
        body: [
          "Role-based page permissions matrix: Full access, View-only or No access per role.",
          "Typical workflow: set up records (vendors, clients, stock items, warehouses) → create purchase order → receive goods (GRN) → update inventory → monitor operations via dashboard.",
          "Common interface actions: Create, Add Vendor, Add Stock, View, Edit, Delete, Save and Cancel — consistently labelled across all modules.",
        ],
        images: [],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 3. SFA CRM APP
  // ══════════════════════════════════════════════════════════════════════════
  {
    slug: "sfa-crm-app",
    title: "SFA CRM App",
    description: "A mobile sales app that lets field teams and trade partners plan visits, order from an e-commerce-style catalogue, track stock and collect payments in one place.",
    file: "/ppts/sfa-crm-app.pptx",
    content: [
      {
        heading: "App at a Glance",
        body: [
          "Parnets SFA-CRM App runs the whole trade cycle on a phone: plan routes, visit trade partners, book orders from a product catalogue, watch stock, collect payments and chase dues.",
          "5 modules, 21 screens, 17 home quick-action tiles.",
          "Typical users: field sales reps (visit, order, collect), trade partners (dealers and distributors), and sales managers (approve plans, track targets).",
        ],
        images: [
          { src: "/product-images/sfa-crm-app/image-1-1.png", alt: "SFA CRM App login and home dashboard" },
        ],
      },
      {
        heading: "Access & Home",
        body: [
          "Login: select company, sign in with email or phone, toggle password visibility, accept Terms & Privacy.",
          "Home Dashboard: personal greeting with today's date, an Attendance Marked badge, KPI strip (Routes, Dues Dr, Alerts, In/Out status) and 17 colourful quick-action tiles covering every module.",
          "Side Menu: profile, FAQ, About Us, Terms, Privacy, Disclaimer, Reach Us and a notification feed with timestamps and unread-count badges.",
        ],
        images: [
          { src: "/product-images/sfa-crm-app/image-2-1.png", alt: "Home dashboard with quick-action tiles" },
        ],
      },
      {
        heading: "Sales & Orders",
        body: [
          "Orders & Products: e-commerce-style catalogue with 344+ pages of products, category filter chips, product cards showing price, stock count and discount badges, Add/Out-of-Stock buttons and a cart badge.",
          "All Vouchers: complete list of invoices and orders with period filters, status chips (pending, approved, processing, dispatched, delivered, completed, cancelled, returned), summary strip and PDF export.",
          "Returns: manage goods coming back with total/approved/pending counters and a status-filter view.",
          "Sales & Collection Target: monthly achievement rings showing Sales and Collections as a percentage achieved, with red target and green achievement tiles.",
        ],
        images: [
          { src: "/product-images/sfa-crm-app/image-5-1.png", alt: "E-commerce order catalogue" },
          { src: "/product-images/sfa-crm-app/image-7-1.png", alt: "All vouchers list" },
          { src: "/product-images/sfa-crm-app/image-8-1.png", alt: "Sales target achievement view" },
        ],
      },
      {
        heading: "Inventory & Products",
        body: [
          "Inventory: four ring counters for total products (6,867+), units available, units sold and low-stock alerts.",
          "Stock Summary: search by product/model/SKU, totals strip (Products, Purchase, Sold, Closing) and PDF/share export.",
          "Product Report: model-number search, all-products list with stock tags, and tap-to-expand detail view.",
        ],
        images: [
          { src: "/product-images/sfa-crm-app/image-9-1.png", alt: "Inventory overview with ring counters" },
          { src: "/product-images/sfa-crm-app/image-10-1.png", alt: "Stock summary and product reports" },
        ],
      },
      {
        heading: "Customers & Finance",
        body: [
          "Trade Partners: searchable directory of dealers, shops and distributors with partner cards and an Add Trade Partner button.",
          "Collection: records every payment received; headline total, cash vs transfer split, partner-wise list with expand-for-details, plus a button to record new collections.",
          "Outstanding Dues: date-range selector, total outstanding (Dr), three rings comparing opening dues, current billing and collected amounts, and per-dealer opening/closing/debit/credit breakdown.",
          "Expenses: log and review day-to-day field expenses with a donut chart, category chips (Petrol, Travel, Phone, Order) and a recent-expenses list.",
        ],
        images: [
          { src: "/product-images/sfa-crm-app/image-12-1.png", alt: "Trade partners directory" },
          { src: "/product-images/sfa-crm-app/image-14-1.png", alt: "Outstanding dues per dealer" },
          { src: "/product-images/sfa-crm-app/image-15-1.png", alt: "Collection payment records" },
        ],
      },
      {
        heading: "Field Force",
        body: [
          "Attendance: colour-coded monthly calendar (Present, Absent, Holiday, Leave) with working-days counter and recap tiles.",
          "Leaves: apply for leave, track approval status (Approved, Pending, Rejected).",
          "Visits: log every customer visit; total visits ring, completed and on-hold counters.",
          "Root Plan (Beat Plan): daily route planner with scrollable week-day strip; approved plans trigger a push notification.",
          "Odometer: KM travelled, visits made and last-visit time per day; journey ring showing KM covered against a 200 KM max.",
          "Activities: task list with priority chips (Low, Medium, High) and a recent-activities feed.",
        ],
        images: [
          { src: "/product-images/sfa-crm-app/image-17-1.png", alt: "Field force attendance calendar" },
        ],
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════════════════
  // 4. SFA CRM (WEB ADMIN PANEL)
  // ══════════════════════════════════════════════════════════════════════════
  {
    slug: "sfa-crm",
    title: "SFA CRM",
    description: "One admin panel to run your field sales team, dealer network and e-commerce storefront, synced with Tally.",
    file: "/ppts/sfa-crm.pptx",
    content: [
      {
        heading: "What is Parnets SFA-CRM?",
        body: [
          "One admin panel to run your field sales team, your dealer network and your e-commerce storefront, synced with Tally.",
          "Sales executives work from the mobile app; managers and sub-admins control everything from this web panel.",
          "Live figures from the admin panel: 13,704 trade partners, 6,867 products, 4,149 collections recorded, ₹16.25 Cr collected.",
          "Key capabilities: mobile app for field executives, two-way Tally sync, role-based sub-admin access and push notifications to the team.",
        ],
        images: [
          { src: "/product-images/sfa-crm/image2.png", alt: "SFA CRM admin panel overview" },
        ],
      },
      {
        heading: "Core Access & Dashboard",
        body: [
          "Secure login with email and password; only authorised users reach the panel.",
          "Sub-Admin Management: add or edit sub-admins with name, email and phone; assign module-level permissions via the shield icon.",
          "Dashboard: colour-coded KPI cards for employees, parties, products and categories; monthly trend line chart (dealers vs products added); donut and bar charts for employee status, top brands and category distribution.",
        ],
        images: [
          { src: "/product-images/sfa-crm/image-11-1.png", alt: "Dashboard KPI cards and charts" },
        ],
      },
      {
        heading: "Catalogue & Partners",
        body: [
          "Product Management: categories and brands keep the catalogue organised; product list shows rate, discount, scheme, colours, stock and web toggle; import from Excel or Tally, sync to Tally.",
          "Inventory: live stock per product with In Stock and Low Stock flags for proactive reordering.",
          "Trade Partners (Dealer Network): manage 13,704+ dealers by business type (Wholesale, Trading, etc.), search by name/code/mobile, set credit limits, import from or sync to Tally.",
          "Outstanding Report: opening balance, current total, collected and outstanding per dealer for any date range.",
        ],
        images: [
          { src: "/product-images/sfa-crm/image-13-1.png", alt: "Product catalogue and inventory management" },
          { src: "/product-images/sfa-crm/image-14-1.png", alt: "Dealer network and outstanding report" },
        ],
      },
      {
        heading: "Field Sales Management",
        body: [
          "Activities: create activity types (Distribution, Manufacturing, Building Materials Trading) and log each customer interaction with priority, type, date, time and status.",
          "Target Management: set sales and collection targets per executive with start/end dates and amounts; view Target List switching between Sales and Collection.",
          "Parties Visits & Root Plan: every visit is logged with employee, trade partner, purpose and location; managers approve route plans before the day begins.",
          "Attendance & Leave: attendance with date, check-in time and address; define leave types and manage approval workflow.",
          "Employees List: full profiles with photo, role, contact, live GPS map (All Employees) and Excel export.",
        ],
        images: [
          { src: "/product-images/sfa-crm/image-15-1.png", alt: "Field sales and visit tracking" },
          { src: "/product-images/sfa-crm/image6.png", alt: "Employee profiles and live map" },
        ],
      },
      {
        heading: "Order-to-Cash",
        body: [
          "Orders: every order with unique ID, invoice number, trade partner, employee, amount and Tally sync status; completed orders are locked for accounting consistency.",
          "Collections: 4,149 collections worth ₹16.25 Cr in the sample data; invoice-level view with approval status, Tally sync, import and Excel export.",
          "Returns: return number (RTN-001 format), employee, trade partner, reason (damaged goods, wrong product, not available) and status from Pending to resolved.",
          "Expenses: expense types and a per-expense log with date, amount, category, receipt image and employee; sync to Tally and Excel export.",
          "Incentive Plans: Hybrid, Turnover-wise and Product-wise plan types with effective dates and on/off toggles.",
        ],
        images: [
          { src: "/product-images/sfa-crm/image-21-2.png", alt: "Orders and collections management" },
          { src: "/product-images/sfa-crm/image-22-2.png", alt: "Returns and expense management" },
        ],
      },
      {
        heading: "Insights, E-commerce & Integrations",
        body: [
          "Reports & Analytics: Target vs Achievement, date-range comparison of executive results, performance status badges (Below Target) and Excel export.",
          "Activity Logs: full audit trail — login history with date, time, role and IP address; filter and export for security reviews.",
          "Web Management (E-commerce): create hero banners with title, subtitle, badge and Shop Now button; manage featured categories and products; control what customers see online with the Show on Website toggle.",
          "Announcements & Notifications: publish team announcements and push notifications (Scheme Announcement and other types) directly to mobile devices.",
          "Integrations: Tally Configuration for import/sync of products, dealers, orders, collections and expenses; Firebase Configuration for web and mobile push notifications.",
        ],
        images: [],
      },
    ],
  },
  // ══════════════════════════════════════════════════════════════════════════
  // 5. HOTEL POS
  // ══════════════════════════════════════════════════════════════════════════
  {
    slug: "hotel-pos",
    title: "Hotel POS",
    description: "Complete hotel point-of-sale system — room management, billing, restaurant orders, housekeeping and guest check-in/check-out in one platform.",
    file: "/ppts/hotel-pos.pptx",
    content: [
      {
        heading: "What is Hotel POS?",
        body: [
          "Hotel POS is an all-in-one property management and point-of-sale system built for hotels, resorts and guest houses.",
          "It manages the full guest lifecycle — from reservation and check-in to room service, restaurant billing, housekeeping and check-out.",
          "The system covers front desk operations, room management, restaurant & bar POS, housekeeping tracking, billing and reports in a single connected platform.",
        ],
        images: [
          { src: "/product-images/hotel-pos/image29.png", alt: "Hotel POS dashboard overview" },
          { src: "/product-images/hotel-pos/image15.png", alt: "Hotel POS main interface" },
        ],
      },
      {
        heading: "Front Desk & Reservations",
        body: [
          "Manage room bookings with real-time availability view across room types and floor plans.",
          "Quick check-in and check-out with guest profile, ID capture and payment settlement.",
          "Handle walk-in guests, advance bookings and online reservation sync.",
        ],
        images: [
          { src: "/product-images/hotel-pos/image17.png", alt: "Front desk and reservations" },
          { src: "/product-images/hotel-pos/image18.png", alt: "Room booking interface" },
        ],
      },
      {
        heading: "Restaurant & Bar POS",
        body: [
          "Table management with live order status — open, occupied, billed and cleared.",
          "KOT (Kitchen Order Ticket) printing directly to kitchen printer on order placement.",
          "Support for room service orders linked directly to guest folios for consolidated billing.",
        ],
        images: [
          { src: "/product-images/hotel-pos/image20.png", alt: "Restaurant POS table management" },
          { src: "/product-images/hotel-pos/image22.png", alt: "Kitchen order management" },
        ],
      },
      {
        heading: "Housekeeping & Room Management",
        body: [
          "Room status dashboard — clean, dirty, occupied, vacant and maintenance.",
          "Assign housekeeping tasks to staff with priority and completion tracking.",
          "Mini-bar consumption tracking automatically added to guest folio.",
        ],
        images: [
          { src: "/product-images/hotel-pos/image25.png", alt: "Housekeeping management" },
          { src: "/product-images/hotel-pos/image26.png", alt: "Room status dashboard" },
        ],
      },
      {
        heading: "Billing & Reports",
        body: [
          "Consolidated guest folio — room charges, restaurant, laundry, mini-bar and extras on one bill.",
          "Support for split billing, company billing and advance payment adjustments.",
          "GST-compliant tax calculation and invoice generation.",
          "Reports: occupancy, revenue by department, daily sales summary and shift reports.",
        ],
        images: [
          { src: "/product-images/hotel-pos/image27.png", alt: "Billing and invoicing" },
          { src: "/product-images/hotel-pos/image35.png", alt: "Reports and analytics" },
        ],
      },
    ],
  },
];

export default productsData;
